import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import {
  UserService,
  UserDTO,
  CreateUserRequest,
  UpdateUserRequest,
  PermissionDTO
} from '../../services/user.service';
import { PermissionService } from '../../services/permission.service';
import { RoleService, RoleDTO } from '../../services/role.service';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';
import { AvatarUploaderComponent } from '../shared/avatar-uploader/avatar-uploader.component';
import type { AvatarCropResult } from '../shared/avatar-uploader/avatar-crop.model';
import { finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TabsModule,
    TooltipModule,
    CheckboxModule,
    InputGroupModule,
    InputGroupAddonModule,
    EntityAuditPanelComponent,
    AvatarUploaderComponent
  ]
})
export class UserFormComponent {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private permissionService = inject(PermissionService);
  private roleService = inject(RoleService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);

  userForm: FormGroup;
  loading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingUserId = signal<number | null>(null);
  userState = signal<'ACTIVE' | 'INACTIVE'>('ACTIVE');

  activeTab = signal<string>('generales');

  tabs = [
    { value: 'generales', label: 'Generales', icon: 'pi pi-user' },
    { value: 'permisos', label: 'Permisos', icon: 'pi pi-key' },
    { value: 'roles', label: 'Roles', icon: 'pi pi-sitemap' },
    { value: 'auditoria', label: 'Auditoría', icon: 'pi pi-history' }
  ] as const;

  /** Lista de permisos disponibles con indicador de si está asignado al usuario */
  permissionsList = signal<Array<PermissionDTO & { isAssigned: boolean }>>([]);
  permissionsLoading = signal(false);

  /** Lista de roles disponibles con indicador de si está asignado al usuario */
  rolesList = signal<Array<RoleDTO & { isAssigned: boolean }>>([]);
  rolesLoading = signal(false);

  /** Tipo del usuario actual (OWNER/USER) para ocultar permisos/roles en OWNER */
  currentUserType = signal<'OWNER' | 'USER' | undefined>(undefined);

  /** Valores de roles cuyo detalle de permisos está expandido */
  expandedRolePermissions = signal<Set<string>>(new Set());

  /** Mostrar u ocultar contraseña en el input (solo creación) */
  passwordVisible = signal(false);

  /** Preview / pendiente de subida de avatar */
  avatarPreviewUrl = signal<string | null>(null);
  avatarBase64 = signal<string | null>(null);
  avatarContentType = signal<string>('image/jpeg');

  /** Guardar habilitado solo cuando nombre, apellido y email son válidos (signal para OnPush) */
  canSave = signal(false);

  constructor() {
    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', [Validators.required, Validators.maxLength(50)]],
      lastname: ['', [Validators.required, Validators.maxLength(30)]],
      surname: ['', [Validators.maxLength(30)]],
      jobTitle: ['', [Validators.maxLength(150)]],
      phone: ['', [Validators.maxLength(10), Validators.pattern(/^$|^\d{10}$/)]],
      password: ['']
    });
    this.updateCanSave();
    this.userForm.valueChanges.subscribe(() => this.updateCanSave());
    this.userForm.statusChanges.subscribe(() => this.updateCanSave());

    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      if (idParam && idParam !== 'nuevo') {
        const userId = Number(idParam);
        if (!Number.isNaN(userId)) {
          this.isEditMode.set(true);
          this.editingUserId.set(userId);
          this.userForm.get('email')?.clearValidators();
          this.userForm.get('email')?.updateValueAndValidity();
          this.userForm.get('password')?.clearValidators();
          this.userForm.get('password')?.updateValueAndValidity();
          this.loadUser(userId);
          this.loadPermissions(userId);
          this.loadRoles(userId);
          return;
        }
      }
      this.isEditMode.set(false);
      this.editingUserId.set(null);
      this.userState.set('ACTIVE');
      this.permissionsList.set([]);
      this.rolesList.set([]);
      this.currentUserType.set(undefined);
      this.userForm.get('password')?.setValidators([Validators.maxLength(50)]);
      this.userForm.get('password')?.updateValueAndValidity();
      this.userForm.get('email')?.setValidators([Validators.required, Validators.email]);
      this.userForm.get('email')?.updateValueAndValidity();
      this.passwordVisible.set(false);
      this.avatarPreviewUrl.set(null);
      this.avatarBase64.set(null);
      this.avatarContentType.set('image/jpeg');
      this.userForm.reset({
        email: '',
        name: '',
        lastname: '',
        surname: '',
        jobTitle: '',
        phone: '',
        password: ''
      });
    });
  }

  private updateCanSave(): void {
    const name = this.userForm.get('name');
    const lastname = this.userForm.get('lastname');
    const email = this.userForm.get('email');
    const phone = this.userForm.get('phone');
    this.canSave.set((name?.valid && lastname?.valid && email?.valid && phone?.valid) ?? false);
  }

  onSubmit(): void {
    if (!this.canSave()) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    const formValue = this.userForm.getRawValue();
    const isEdit = this.isEditMode() && this.editingUserId() !== null;

    if (isEdit) {
      const selectedPermissions = this.getSelectedPermissionEnumNames();
      const selectedRoles = this.getSelectedRoleEnumNames();
      const request: UpdateUserRequest = {
        name: formValue.name || undefined,
        lastname: formValue.lastname || undefined,
        surname: formValue.surname || undefined,
        phone: this.sanitizePhone(formValue.phone) || undefined,
        jobTitle: formValue.jobTitle || undefined,
        state: 'ACTIVE',
        permissions: selectedPermissions.length > 0 ? selectedPermissions : undefined,
        roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        avatarBase64: this.avatarBase64() ?? undefined,
        avatarContentType: this.avatarBase64() ? this.avatarContentType() : undefined
      };
      this.userService
        .updateUser(this.editingUserId()!, request)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (response) => {
            if (response.ok) {
              this.toastr.success('Usuario actualizado correctamente');
              this.avatarBase64.set(null);
              if (response.body?.avatarUrl) {
                this.avatarPreviewUrl.set(response.body.avatarUrl);
              }
            } else {
              this.error.set(response.message || 'Error al actualizar el usuario');
              this.toastr.error(response.message);
            }
          },
          error: err => {
            this.error.set(err.error?.message || 'Error al conectar con el servidor');
            this.toastr.error(err.error?.message || 'Error al actualizar el usuario');
          }
        });
    } else {
      const request: CreateUserRequest = {
        email: formValue.email,
        name: formValue.name,
        password: formValue.password,
        lastname: formValue.lastname || undefined,
        surname: formValue.surname || undefined,
        phone: this.sanitizePhone(formValue.phone) || undefined,
        jobTitle: formValue.jobTitle || undefined,
        avatarBase64: this.avatarBase64() ?? undefined,
        avatarContentType: this.avatarBase64() ? this.avatarContentType() : undefined
      };
      this.userService
        .createUser(request)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (response) => {
            if (response.ok && response.body?.id) {
              this.toastr.success('Usuario creado correctamente');
              this.router.navigate(['/users', response.body.id], { replaceUrl: true });
            } else {
              this.error.set(response.message || 'Error al crear el usuario');
              this.toastr.error(response.message);
            }
          },
          error: err => {
            this.error.set(err.error?.message || 'Error al conectar con el servidor');
            this.toastr.error(err.error?.message || 'Error al crear el usuario');
          }
        });
    }
  }

  cancel(): void {
    this.router.navigate(['/users']);
  }

  onTabChange(value: string | number | undefined): void {
    const tab = value == null ? 'generales' : String(value);
    this.activeTab.set(tab);
  }

  readonly statusTooltip = 'Estado del usuario: Activo o Inactivo.';

  statusDisplayLabel(): string {
    return this.userState() === 'INACTIVE' ? 'Inactivo' : 'Activo';
  }

  /** Abre en nueva pestaña la página de Keycloak para cambiar/recuperar contraseña */
  openResetPasswordInKeycloak(): void {
    window.open(this.authService.getResetCredentialsUrl(), '_blank', 'noopener,noreferrer');
  }

  /** Genera una contraseña aleatoria y la asigna al formulario (12 caracteres: letras y números) */
  generatePassword(): void {
    const length = 12;
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let result = '';
    const random = (max: number) => Math.floor(Math.random() * max);
    for (let i = 0; i < length; i++) {
      result += chars[random(chars.length)];
    }
    this.userForm.patchValue({ password: result });
    this.passwordVisible.set(true);
  }

  togglePasswordVisible(): void {
    this.passwordVisible.update(v => !v);
  }

  /** Solo dígitos en el teléfono (máx. 10). */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = this.sanitizePhone(input.value);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.userForm.get('phone')?.setValue(digits, { emitEvent: true });
  }

  private sanitizePhone(value: string | null | undefined): string {
    return (value ?? '').replace(/\D/g, '').slice(0, 10);
  }

  copyPasswordToClipboard(): void {
    const password = this.userForm.get('password')?.value;
    if (!password) return;
    navigator.clipboard.writeText(password).then(
      () => this.toastr.success('Contraseña copiada al portapapeles'),
      () => this.toastr.error('No se pudo copiar')
    );
  }

  private loadUser(userId: number): void {
    this.loading.set(true);
    this.userService.getUserById(userId).subscribe({
      next: response => {
        if (response.ok) {
          this.patchUser(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el usuario');
        }
        this.loading.set(false);
      },
      error: err => {
        this.error.set('Error al cargar el usuario. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
      }
    });
  }

  private patchUser(user: UserDTO): void {
    this.currentUserType.set(user.type ?? undefined);
    this.userState.set(user.state === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');
    this.avatarPreviewUrl.set(user.avatarUrl || null);
    this.avatarBase64.set(null);
    this.userForm.patchValue({
      email: user.email || '',
      name: user.name || '',
      lastname: user.lastname || '',
      surname: user.surname || '',
      jobTitle: user.jobTitle || '',
      phone: this.sanitizePhone(user.phone),
      password: ''
    });
  }

  onAvatarChange(result: AvatarCropResult): void {
    this.avatarBase64.set(result.base64);
    this.avatarContentType.set(result.contentType);
    this.avatarPreviewUrl.set(result.previewUrl);
  }

  private loadPermissions(userId: number): void {
    this.permissionsLoading.set(true);
    forkJoin({
      all: this.permissionService.getPermissions(),
      user: this.userService.getUserPermissions(userId)
    })
      .pipe(finalize(() => this.permissionsLoading.set(false)))
      .subscribe({
        next: ({ all, user }) => {
          const allPerms = all.ok ? all.body : [];
          const userPermValues = user.ok ? new Set(user.body.map(p => p.value)) : new Set<string>();
          const merged = allPerms.map(p => ({
            ...p,
            isAssigned: userPermValues.has(p.value)
          }));
          this.permissionsList.set(merged);
        },
        error: () => {
          this.permissionsList.set([]);
        }
      });
  }

  togglePermission(item: PermissionDTO & { isAssigned: boolean }): void {
    this.setPermissionAssigned(item, !item.isAssigned);
  }

  setPermissionAssigned(item: PermissionDTO & { isAssigned: boolean }, assigned: boolean): void {
    this.permissionsList.update(list =>
      list.map(p => (p.value === item.value ? { ...p, isAssigned: assigned } : p))
    );
  }

  /** Agrupa permisos por contexto (parte antes de ":") */
  permissionsByContext(): Array<{ context: string; label: string; permissions: Array<PermissionDTO & { isAssigned: boolean }> }> {
    const list = this.permissionsList();
    const map = new Map<string, Array<PermissionDTO & { isAssigned: boolean }>>();
    for (const p of list) {
      const context = p.value.includes(':') ? p.value.split(':')[0] : 'otros';
      if (!map.has(context)) map.set(context, []);
      map.get(context)!.push(p);
    }
    return Array.from(map.entries()).map(([context, permissions]) => ({
      context,
      label: context.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      permissions
    }));
  }

  /** Convierte value (ej. "user:read") a nombre enum (ej. "USER_READ") para la API */
  private valueToEnumName(value: string): string {
    return value.replace(/-/g, '_').replace(/:/g, '_').toUpperCase();
  }

  private getSelectedPermissionEnumNames(): string[] {
    return this.permissionsList()
      .filter(p => p.isAssigned)
      .map(p => this.valueToEnumName(p.value));
  }

  private loadRoles(userId: number): void {
    this.rolesLoading.set(true);
    forkJoin({
      all: this.roleService.getRoles(),
      user: this.roleService.getUserRoles(userId)
    })
      .pipe(finalize(() => this.rolesLoading.set(false)))
      .subscribe({
        next: ({ all, user }) => {
          const allRoles = all.ok ? all.body : [];
          const userRoleValues = user.ok ? new Set(user.body.map(r => r.value)) : new Set<string>();
          const merged = allRoles.map(r => ({
            ...r,
            isAssigned: userRoleValues.has(r.value)
          }));
          this.rolesList.set(merged);
        },
        error: () => {
          this.rolesList.set([]);
        }
      });
  }

  toggleRole(item: RoleDTO & { isAssigned: boolean }): void {
    this.setRoleAssigned(item, !item.isAssigned);
  }

  setRoleAssigned(item: RoleDTO & { isAssigned: boolean }, assigned: boolean): void {
    this.rolesList.update(list =>
      list.map(r => (r.value === item.value ? { ...r, isAssigned: assigned } : r))
    );
  }

  /** Agrupa roles por global (global vs no global) para consistencia con permisos */
  rolesByGroup(): Array<{ label: string; roles: Array<RoleDTO & { isAssigned: boolean }> }> {
    const list = this.rolesList();
    const global = list.filter(r => r.global);
    const others = list.filter(r => !r.global);
    const groups: Array<{ label: string; roles: Array<RoleDTO & { isAssigned: boolean }> }> = [];
    if (global.length) groups.push({ label: 'Globales', roles: global });
    if (others.length) groups.push({ label: 'Por proyecto', roles: others });
    return groups.length ? groups : [{ label: 'Roles', roles: list }];
  }

  private getSelectedRoleEnumNames(): string[] {
    return this.rolesList()
      .filter(r => r.isAssigned)
      .map(r => this.roleService.roleValueToEnumName(r.value));
  }

  isRolePermissionsExpanded(roleValue: string): boolean {
    return this.expandedRolePermissions().has(roleValue);
  }

  toggleRolePermissionsExpanded(roleValue: string, $event?: Event): void {
    if ($event) $event.stopPropagation();
    this.expandedRolePermissions.update(set => {
      const next = new Set(set);
      if (next.has(roleValue)) next.delete(roleValue);
      else next.add(roleValue);
      return next;
    });
  }
}
